/* Shared inventory invalidation and GET scheduling for inline product cards.
   Each consumer owns its selection and AbortSignal; only response text is shared. */
(() => {
  if (window.Eurus.productCardRefresh) return;
  const cards = new Map();
  const clearListeners = new Map();
  const revisions = new Map();
  const requests = new Map();
  const queue = [];
  let generation = 0, active = 0, timer;
  const revision = id => `${generation}:${revisions.get(String(id)) || 0}`;
  const abortError = () => new DOMException('Card request cancelled', 'AbortError');

  function pump() {
    while (active < 4 && queue.length) {
      const task = queue.shift();
      if (!task.consumers.size) continue;
      active++;
      task.started = true;
      (async () => {
        try {
          const response = await fetch(task.url, { signal: task.controller.signal });
          if (!response.ok) throw new Error(`Variant request failed: ${response.status}`);
          const text = await response.text();
          for (const consumer of [...task.consumers]) consumer.finish(null, text);
        } catch (error) {
          for (const consumer of [...task.consumers]) consumer.finish(error);
        } finally {
          if (requests.get(task.key) === task) requests.delete(task.key);
          active--;
          pump();
        }
      })();
    }
  }

  function load(url, productId, signal) {
    if (signal.aborted) return Promise.reject(abortError());
    const key = `${revision(productId)}:${url}`;
    let task = requests.get(key);
    if (!task) {
      task = { key, url, controller: new AbortController(), consumers: new Set(), started: false };
      requests.set(key, task);
      queue.push(task);
    }
    return new Promise((resolve, reject) => {
      const consumer = {
        finish(error, text) {
          signal.removeEventListener('abort', cancel);
          task.consumers.delete(consumer);
          if (error) reject(error); else resolve(text);
        }
      };
      const cancel = () => {
        consumer.finish(abortError());
        if (!task.consumers.size) {
          if (requests.get(key) === task) requests.delete(key);
          task.controller.abort();
          if (!task.started) {
            const index = queue.indexOf(task);
            if (index >= 0) queue.splice(index, 1);
          }
        }
      };
      task.consumers.add(consumer);
      signal.addEventListener('abort', cancel, { once: true });
      pump();
    });
  }

  function visible(element) {
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0 &&
      rect.top < window.innerHeight && rect.left < window.innerWidth;
  }

  async function ensure(record) {
    while (cards.get(record.element) === record && record.element.isConnected) {
      if (!record.dirty && !record.running) return true;
      if (!record.running) {
        const version = revision(record.id);
        record.running = Promise.resolve().then(() => cards.get(record.element) === record && record.element.isConnected && record.refresh()).then(success => {
          const current = version === revision(record.id);
          if (current && success) record.dirty = false;
          return { success, current };
        }).catch(() => ({ success: false, current: version === revision(record.id) }));
      }
      const pending = record.running;
      const result = await pending;
      if (record.running === pending) record.running = null;
      if (!result.success && result.current) return false;
    }
    return false;
  }

  function schedule() {
    if (timer !== undefined) return;
    timer = setTimeout(() => {
      timer = undefined;
      for (const record of cards.values()) {
        if (record.dirty && visible(record.element)) void ensure(record);
      }
    }, 0);
  }

  function invalidate(productIds) {
    const ids = Array.isArray(productIds) ? new Set(productIds.map(String)) : null;
    if (ids) for (const id of ids) revisions.set(id, (revisions.get(id) || 0) + 1);
    else generation++;
    for (const record of cards.values()) {
      if (ids && !ids.has(record.id)) continue;
      record.dirty = true;
      record.invalidate();
    }
    schedule();
  }

  const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    for (const entry of entries) {
      const record = cards.get(entry.target);
      if (entry.isIntersecting && record?.dirty) void ensure(record);
    }
  }) : null;
  const onCartChange = event => invalidate(event.detail?.productIds);
  document.addEventListener('eurus:cart:items-changed', onCartChange);
  // The fallback also covers browsers without IntersectionObserver.
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });

  window.Eurus.productCardRefresh = {
    load,
    invalidate,
    isFresh(element) {
      const record = cards.get(element);
      return !record || (!record.dirty && !record.running);
    },
    ensureFresh(element) {
      const record = cards.get(element);
      return record ? ensure(record) : Promise.resolve(element.isConnected !== false);
    },
    register(element, productId, hooks) {
      const record = { element, id: String(productId), ...hooks, dirty: false, running: null };
      cards.set(element, record);
      let listener = clearListeners.get(record.id);
      if (!listener) {
        listener = { count: 0, clear: invalidate.bind(null, [record.id]) };
        clearListeners.set(record.id, listener);
        document.addEventListener(`eurus:product-card:clear:${record.id}`, listener.clear);
      }
      listener.count++;
      const interact = event => {
        // Submission performs its own awaited check; don't disable a button
        // between pointerdown/focus and its click.
        if (!event.target.closest('[data-inline-card-atc]') && record.dirty) void ensure(record);
      };
      element.addEventListener('pointerdown', interact);
      element.addEventListener('focusin', interact);
      observer?.observe(element);
      return () => {
        cards.delete(element);
        observer?.unobserve(element);
        if (--listener.count === 0) {
          document.removeEventListener(`eurus:product-card:clear:${record.id}`, listener.clear);
          clearListeners.delete(record.id);
        }
        element.removeEventListener('pointerdown', interact);
        element.removeEventListener('focusin', interact);
        record.invalidate();
      };
    }
  };
})();
